import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight, Heart, Check } from 'lucide-react';
import { toast } from 'sonner';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setIsSubscribed(true);
    toast.success('Successfully subscribed! 🎉', {
      description: 'You\'ll receive the best deals in your inbox.'
    });
    setEmail('');
    setTimeout(() => setIsSubscribed(false), 3000);
  };
  
  return (
    <footer className="bg-slate-900 text-white">
      {/* CTA Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-500">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                Never Miss a Sleep Deal
              </h3>
              <p className="text-red-100">Get the best mattress & bedding coupons delivered to your inbox</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row items-center gap-3">
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="px-5 py-3.5 pr-12 bg-white/20 backdrop-blur-sm rounded-full text-white placeholder-white/70 w-64 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
                  data-testid="subscribe-email-input"
                />
                <Mail className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
              </div>
              <button 
                type="submit"
                disabled={isSubscribed}
                className={`px-6 py-3.5 rounded-full font-bold text-sm transition-all btn-press flex items-center gap-2 ${
                  isSubscribed 
                    ? 'bg-green-400 text-green-900' 
                    : 'bg-white text-red-600 hover:bg-slate-100'
                }`}
                data-testid="subscribe-btn"
              >
                {isSubscribed ? (
                  <>
                    <Check size={16} />
                    Subscribed!
                  </>
                ) : (
                  <>
                    Subscribe
                    <ArrowRight size={16} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">MF</span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-heading font-bold text-lg">Mattressfirm</span>
                <span className="font-heading font-semibold text-xs text-slate-400">Coupons Store</span>
              </div>
            </Link>
            <p className="text-slate-400 text-sm leading-relaxed">
              Your one-stop destination for the best mattress, bed, and bedding deals from top sleep brands.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Explore</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/stores" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  All Stores
                </Link>
              </li>
              <li>
                <Link to="/categories" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Categories
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Blog
                </Link>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/contact" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a href="mailto:batakizzz@gmail.com" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Support Email: batakizzz@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Popular Categories */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Popular</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/category/mattresses" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Mattress Deals
                </Link>
              </li>
              <li>
                <Link to="/category/bed-frames" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Bed Frame Deals
                </Link>
              </li>
              <li>
                <Link to="/category/bedding-sheets" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Bedding Deals
                </Link>
              </li>
              <li>
                <Link to="/category/pillows" className="text-slate-400 text-sm hover:text-red-400 transition-colors">
                  Pillow Deals
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {currentYear} Mattressfirm Coupons Store. All rights reserved.
            </p>
            <p className="text-slate-500 text-sm flex items-center gap-1">
              Made with <Heart size={14} className="text-red-500 fill-red-500" /> for smart shoppers
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
